import React from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Defs, Image as SvgImage, LinearGradient, Mask, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';

/**
 * Fades the list out where it runs behind the header and the bottom nav. The screen is hosted
 * full-bleed, so cards keep scrolling past both and would otherwise show through the gaps
 * around the nav plate and between the header buttons.
 *
 * A real alpha mask over the content would be better, but the only masking library here
 * (@react-native-masked-view) ships a no-op stub for web. So the app backdrop is painted back
 * over the content instead - the same image MainLayout draws, aligned to the viewport and cut to
 * this strip, so it matches whatever is behind it rather than approximating it with a flat
 * colour. The image is what fades: an SVG mask takes it from opaque at the screen edge to
 * nothing over `fadeLength`.
 */
type Props = {
	edge: 'top' | 'bottom';
	/** The backdrop MainLayout is painting, so the two agree. */
	source: any;
	/** Full viewport, so the strip lines up with the backdrop behind it. */
	viewportWidth: number;
	viewportHeight: number;
	/** How far in from the edge the strip reaches. */
	height: number;
	/** How much of that is fully opaque, measured from the screen edge. */
	solid: number;
};

export const EventScrollFade = ({
	edge,
	source,
	viewportWidth,
	viewportHeight,
	height,
	solid,
}: Props) => {
	const maskId = useSvgId('eventFadeMask');
	const gradientId = useSvgId('eventFadeRamp');
	const isTop = edge === 'top';

	// The SVG is only the strip, and the backdrop inside it is laid out at the full viewport and
	// shifted so it still sits exactly where MainLayout drew it. Spanning the whole viewport and
	// cropping in the wrapper looked the same but rasterised the full-screen image through the mask
	// - twice per screen, on the UI thread - just as the screen was opening.
	const solidStop = height > 0 ? Math.min(1, solid / height) : 0;
	const imageY = isTop ? 0 : height - viewportHeight;

	return (
		<View
			pointerEvents='none'
			style={[styles.fade, { height }, isTop ? styles.top : styles.bottom]}
		>
			<Svg width={viewportWidth} height={height}>
				<Defs>
					<LinearGradient
						id={gradientId}
						x1='0'
						y1={isTop ? '0' : '1'}
						x2='0'
						y2={isTop ? '1' : '0'}
					>
						<Stop offset='0' stopColor='#FFFFFF' stopOpacity='1' />
						<Stop offset={String(solidStop)} stopColor='#FFFFFF' stopOpacity='1' />
						<Stop offset='1' stopColor='#FFFFFF' stopOpacity='0' />
					</LinearGradient>
					<Mask id={maskId}>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${gradientId})`} />
					</Mask>
				</Defs>
				<SvgImage
					x='0'
					y={imageY}
					width={viewportWidth}
					height={viewportHeight}
					href={source}
					preserveAspectRatio='xMidYMid slice'
					mask={`url(#${maskId})`}
				/>
			</Svg>
		</View>
	);
};

const styles = StyleSheet.create({
	fade: {
		position: 'absolute',
		left: 0,
		right: 0,
		overflow: 'hidden',
	},
	top: { top: 0 },
	bottom: { bottom: 0 },
});

export default EventScrollFade;
